import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  fetchBriefingCrewNames,
  fetchBriefingFlights,
  type BriefingFlightData,
  type FlightLeg,
  type FlightPassenger,
} from "@produce/shared";

// Producer-side data access for briefing flights.
//
// Everything the Flights dialog does goes through this hook: it owns
// the query key, the cache invalidation and the write shapes, so the
// dialog components stay presentational and the editor page doesn't
// grow yet another inline Supabase query.
//
// Writes go straight to PostgREST rather than through the big
// save_briefing RPC on purpose. Flights are edited in their own modal
// and saved there; funnelling them through the wizard's step save would
// couple two unrelated lifecycles and re-run the whole briefing save on
// every flight tweak.

export const briefingFlightsKey = (briefingId: string | null | undefined) =>
  ["briefing-flights", briefingId ?? ""] as const;

const EMPTY: BriefingFlightData = { legs: [], passengers: [] };

/**
 * Columns a producer may write on a leg. Derived from the shared row
 * type rather than restated: a column added to FlightLeg but forgotten
 * here would silently never reach the database.
 */
export type FlightLegInput = Omit<FlightLeg, "id" | "briefing_id">;

/** Per-person details on a leg. */
export type FlightPassengerInput = Omit<FlightPassenger, "id" | "flight_id">;

/**
 * The briefing's crew, by name — the list the flights UI ticks people
 * off from. One hook so the section, the dialog and the review page
 * share a cache entry instead of three copies of the same query under
 * two slightly different keys.
 */
export function useBriefingCrewNames(briefingId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ["briefing-crew-names", briefingId ?? ""],
    enabled: enabled && !!briefingId,
    queryFn: () => (briefingId ? fetchBriefingCrewNames(supabase, briefingId) : []),
  });
}

export function useBriefingFlights(briefingId: string | null | undefined) {
  const queryClient = useQueryClient();
  const queryKey = briefingFlightsKey(briefingId);

  const query = useQuery({
    queryKey,
    enabled: !!briefingId,
    queryFn: () => (briefingId ? fetchBriefingFlights(supabase, briefingId) : EMPTY),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  /**
   * Create or update one leg together with its passenger list, in that
   * order: the passenger rows need the leg id, and a brand-new leg has
   * none until it is inserted.
   *
   * Passengers are reconciled as a diff (delete removed, upsert kept)
   * instead of delete-all-then-insert. A blanket delete would fire the
   * mobile-invalidate trigger for every passenger on every save, and
   * would briefly leave a crew member without a flight if the second
   * statement failed.
   */
  const saveLeg = useMutation({
    mutationFn: async (input: {
      /** Assigned by the caller before the first save — see below. */
      legId: string;
      leg: FlightLegInput;
      passengers: FlightPassengerInput[];
    }) => {
      if (!briefingId) throw new Error("No briefing selected");

      // Upsert on the primary key, with the id assigned by the caller
      // before the first save. That is what makes saving idempotent:
      // two saves that overlap on the same draft carry the same id and
      // converge on one row. Deriving identity from the server's reply
      // instead is what produced two outbound flights — both saves saw
      // "no id yet" and both inserted.
      const legId = input.legId;
      const { error } = await supabase
        .from("briefing_flights")
        .upsert({ ...input.leg, id: legId, briefing_id: briefingId });
      if (error) throw error;

      // Read the current passenger list from the database rather than
      // the React Query cache: another producer (or a second tab) may
      // have changed it since this dialog opened, and a diff computed
      // against stale data would resurrect or drop the wrong rows.
      const { data: existing, error: existingError } = await supabase
        .from("briefing_flight_passengers")
        .select("id, briefing_crew_id, booking_reference, seat, baggage, special_baggage")
        .eq("flight_id", legId);
      if (existingError) throw existingError;

      const keep = new Set(input.passengers.map((p) => p.briefing_crew_id));
      const before = new Map((existing ?? []).map((row) => [row.briefing_crew_id, row]));

      const removedIds = (existing ?? [])
        .filter((row) => !keep.has(row.briefing_crew_id))
        .map((row) => row.id);

      if (removedIds.length > 0) {
        const { error } = await supabase
          .from("briefing_flight_passengers")
          .delete()
          .in("id", removedIds);
        if (error) throw error;
      }

      // Only rows that actually changed. Rewriting all of them meant
      // correcting one seat on a ten-person flight issued ten updates —
      // and fired the crew push trigger ten times, for nine people
      // whose booking hadn't changed.
      const changed = input.passengers.filter((p) => {
        const row = before.get(p.briefing_crew_id);
        if (!row) return true;
        return (
          row.booking_reference !== p.booking_reference ||
          row.seat !== p.seat ||
          row.baggage !== p.baggage ||
          row.special_baggage !== p.special_baggage
        );
      });

      if (changed.length > 0) {
        const { error } = await supabase.from("briefing_flight_passengers").upsert(
          changed.map((p) => ({ ...p, flight_id: legId })),
          { onConflict: "flight_id,briefing_crew_id" },
        );
        if (error) throw error;
      }

      return legId;
    },
    // Deliberately no onSuccess invalidate: the dialog saves several
    // legs in a row, and invalidating per leg made each save block on a
    // full refetch whose result the next save immediately superseded.
    // The dialog invalidates once when its batch is done.
  });

  /**
   * Persist the Flights on/off toggle on its own, the moment it is
   * flipped.
   *
   * It used to ride along with the wizard's briefing save, which meant a
   * producer could switch Flights on, enter every flight in the dialog,
   * navigate away — and have the toggle stay false in the database. The
   * flights existed but the section was hidden everywhere: not on the
   * review page, not in the crew app. A switch that visibly moves has to
   * be saved when it moves.
   */
  const setEnabled = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!briefingId) return;
      const { error } = await supabase
        .from("briefings")
        .update({ flights_enabled: enabled })
        .eq("id", briefingId);
      if (error) throw error;
    },
  });

  /** Removing a leg cascades to its passenger rows in the database. */
  const deleteLeg = useMutation({
    mutationFn: async (legId: string) => {
      const { error } = await supabase.from("briefing_flights").delete().eq("id", legId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    data: query.data ?? EMPTY,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    saveLeg,
    deleteLeg,
    setEnabled,
    invalidate,
  };
}
